import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import StarekContext from "../../context/StarekContext";
import { SETTING, useSetting, useSettingState } from "../../hooks/useSettings";
import {
    printReports,
    getColorVariant,
    getSignalColorFromDevice,
    getLightColor,
} from "../../utils";
import ReportResults from "./ReportResults";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";

export default function EfficiencyReport({
    filters,
    range,
    fetchRawData,
    onRefresh,
    onBarClick,
    shadowDevices,
    onFavoriteClick,
    onDownloadCSV,
    cachedDatapoints,
    isLoadingDatapoints,
    selectedDeviceIndex,
    onSelectedDeviceIndexChange,
    showThresholds = true,
    onShowThresholdsChange,
}) {
    const { types, auth, lastUsedWorkspace } = useContext(StarekContext);
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [showAiPanel, setShowAiPanel] = useState(false);
    const currentDeviceIndex = selectedDeviceIndex;
    const setCurrentDeviceIndex = onSelectedDeviceIndexChange;
    const [hideEmptyDevices, setHideEmptyDevices] = useState(false);
    // Starting position of the Auto-refresh switch and its interval come from
    // Settings; flipping the switch here only lasts for this visit.
    const [autoRefresh, setAutoRefresh] = useSettingState(
        SETTING.REPORT_AUTO_REFRESH,
        true,
    );
    const autoRefreshMs = useSetting(SETTING.REPORT_AUTO_REFRESH_MS, 60000);
    const [autoRefreshIntervalId, setAutoRefreshIntervalId] = useState(null);
    const [localShowThresholds, setLocalShowThresholds] =
        useState(showThresholds);
    const printRef = useRef(null);

    const defaultSignalColors = types?.device?.defaultSignalColors || [];

    const handleAnalyzeWithAI = async () => {
        try {
            setAiAnalyzing(true);
            setAiError(null);
            setShowAiPanel(true);

            const { scheme, host, port } = lastUsedWorkspace.engine;
            const workspaceId = lastUsedWorkspace.name;

            const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints/ai-summary`;

            const payload = {
                input: "Analyze the device efficiency data and provide insights on trends, anomalies, and recommendations",
                data: rawData,
            };

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            console.log("AI Analysis Result:", result);
            setAiResults(result);
        } catch (error) {
            console.error("Error analyzing with AI:", error);
            setAiError("Failed to analyze data with AI. Please try again.");
            setAiResults(null);
        } finally {
            setAiAnalyzing(false);
        }
    };

    const handleDownloadCSV = () => {
        if (onDownloadCSV) onDownloadCSV();
    };

    const loadData = async () => {
        if (onRefresh) {
            const didUpdate = await onRefresh();
            if (didUpdate) return;
        }

        if (fetchRawData) {
            setLoading(true);
            fetchRawData()
                .then((data) => {
                    if (data && data.success) {
                        setRawData(data.data);
                        setCurrentDeviceIndex(0);
                    } else {
                        setRawData(null);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch raw data", err);
                    setRawData(null);
                })
                .finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        setLoading(isLoadingDatapoints);
        if (cachedDatapoints && cachedDatapoints.success) {
            setRawData(cachedDatapoints.data);
            // Don't reset device index here - let parent manage it for persistence
        }
    }, [cachedDatapoints, isLoadingDatapoints]);

    // Setup auto-refresh interval
    useEffect(() => {
        if (autoRefresh && fetchRawData) {
            const intervalId = setInterval(() => {
                fetchRawData().catch((err) =>
                    console.error("Auto-refresh failed:", err),
                );
            }, autoRefreshMs);
            setAutoRefreshIntervalId(intervalId);

            return () => {
                if (intervalId) clearInterval(intervalId);
            };
        } else {
            // Clear interval if auto-refresh is disabled
            if (autoRefreshIntervalId) {
                clearInterval(autoRefreshIntervalId);
                setAutoRefreshIntervalId(null);
            }
        }
    }, [autoRefresh, fetchRawData, autoRefreshMs]);

    const allDevices = useMemo(() => {
        // Use shadowDevices as the master list
        if (!shadowDevices || shadowDevices.length === 0) return [];
        if (!filters?.devices) return shadowDevices.map((d) => d.name || d.id);

        const selectedDeviceNames = filters.devices
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean);

        return shadowDevices
            .filter((dev) => {
                const deviceName = dev.name || dev.id;
                return selectedDeviceNames.includes(deviceName);
            })
            .map((d) => d.name || d.id);
    }, [shadowDevices, filters]);

    console.log(
        "EfficiencyReport - rawData keys:",
        rawData ? Object.keys(rawData) : "no rawData",
    );
    console.log("EfficiencyReport - allDevices:", allDevices);
    console.log("EfficiencyReport - currentDeviceIndex:", currentDeviceIndex);

    // Validate and clamp currentDeviceIndex to valid range
    const validatedDeviceIndex = useMemo(() => {
        if (allDevices.length === 0) return 0;
        return Math.min(currentDeviceIndex, allDevices.length - 1);
    }, [currentDeviceIndex, allDevices]);

    const deviceList = useMemo(() => {
        if (!hideEmptyDevices) return allDevices;
        return allDevices.filter((deviceName) => {
            const deviceSignals = rawData?.[deviceName];
            if (!deviceSignals) return false;
            // Check if device has any data points
            return Object.values(deviceSignals).some(
                (signals) => Array.isArray(signals) && signals.length > 0,
            );
        });
    }, [allDevices, rawData, hideEmptyDevices]);

    const currentDeviceName = deviceList[validatedDeviceIndex];

    // Get current device from shadowDevices (master source of truth)
    const currentShadowDevice = useMemo(() => {
        if (!currentDeviceName || !shadowDevices) return null;
        return shadowDevices.find(
            (dev) => (dev.name || dev.id) === currentDeviceName,
        );
    }, [currentDeviceName, shadowDevices]);

    const chartData = useMemo(() => {
        try {
            if (
                !currentDeviceName ||
                !currentShadowDevice ||
                !rawData?.[currentDeviceName]
            ) {
                return [];
            }

            const deviceSignalsData = rawData[currentDeviceName];
            const defaultSignalColors =
                types?.device?.defaultSignalColors || [];

            // Use runtimeSignals from shadowDevice as master list
            const runtimeSignals =
                currentShadowDevice.device?.runtime?.signals || [];
            const signalKeys = Array.isArray(runtimeSignals)
                ? runtimeSignals.map((s) => s.name)
                : [];
            console.log(
                "EfficiencyReport chartData - using shadowDevice signals:",
                signalKeys,
            );

            return signalKeys.map((signalKey, idx) => {
                // signalKey is from shadowDevice.device.runtime.signals.name
                const points = deviceSignalsData[signalKey] || [];

                let totalPoints = points.length;

                let buckets = {
                    off: 0,
                    on: 0,
                    b10: 0,
                    b20: 0,
                    b30: 0,
                    b40: 0,
                    b50: 0,
                    b60: 0,
                    b70: 0,
                    b80: 0,
                    b90: 0,
                    b100: 0,
                };

                // For now, assume analog unless we can detect it's digital
                // Digital signals typically have values 0 or 1
                const isDigital =
                    points.length > 0 &&
                    points.every(
                        (p) => p.value === 0 || p.value === 1,
                    );

                // Get signal parameters for threshold-based snapping
                const runtimeSignal = runtimeSignals?.find(
                    (s) => s.name === signalKey,
                );
                const signalParams = runtimeSignal?.parameters || {};
                const minThreshold = signalParams?.minThreshold ?? 50;
                const maxThreshold = signalParams?.maxThreshold ?? 50;
                const minThresholdValue = (minThreshold / 100) * 1023;
                const maxThresholdValue = (maxThreshold / 100) * 1023;

                console.log(
                    `Signal "${signalKey}": minThreshold=${minThreshold}%, maxThreshold=${maxThreshold}%, values: min=${Math.min(...points.map((p) => p.value))}, max=${Math.max(...points.map((p) => p.value))}`,
                );

                const useThresholds = !isDigital && localShowThresholds;

                if (isDigital) {
                    // Digital signals: standard on/off counting
                    points.forEach((p) => {
                        if (p.value > 0) buckets.on++;
                        else buckets.off++;
                    });
                } else if (useThresholds) {
                    // Analog with thresholds: snap to binary states
                    // Track state over time - values between thresholds keep previous state
                    let currentState = "off"; // Start in LOW/OFF state

                    points.forEach((p) => {
                        const val = p.value;

                        // Update state based on threshold boundaries
                        if (val > maxThresholdValue) {
                            currentState = "on";
                        } else if (val < minThresholdValue) {
                            currentState = "off";
                        }
                        // Else: state stays the same (intermediate values)

                        // Count this point in the current state
                        if (currentState === "on") {
                            buckets.on++;
                        } else {
                            buckets.off++;
                        }
                    });

                    console.log(
                        `Signal "${signalKey}" with thresholds: on=${buckets.on}, off=${buckets.off}`,
                    );
                } else {
                    // Analog without thresholds: percentile distribution
                    points.forEach((p) => {
                        const val = p.value;
                        const pct = (val / 1023) * 100;
                        if (pct <= 10) buckets.b10++;
                        else if (pct <= 20) buckets.b20++;
                        else if (pct <= 30) buckets.b30++;
                        else if (pct <= 40) buckets.b40++;
                        else if (pct <= 50) buckets.b50++;
                        else if (pct <= 60) buckets.b60++;
                        else if (pct <= 70) buckets.b70++;
                        else if (pct <= 80) buckets.b80++;
                        else if (pct <= 90) buckets.b90++;
                        else buckets.b100++;
                    });
                }

                const result = {
                    name: signalKey,
                    // When analog with thresholds enabled, render as digital (on/off) type
                    type: useThresholds
                        ? "digital"
                        : isDigital
                          ? "digital"
                          : "analog",
                    baseColor: getSignalColorFromDevice(
                        currentShadowDevice,
                        signalKey,
                        idx,
                        defaultSignalColors,
                    ),
                };

                // Handle empty signals - mark with noData flag
                if (totalPoints === 0) {
                    result.noData = true;
                    // Set one bucket to 100 so bars render at full height with hatching
                    // For digital: use 'off', for analog: use 'b100'
                    const bucketToFill = isDigital ? "off" : "b100";
                    Object.keys(buckets).forEach((key) => {
                        result[key] = key === bucketToFill ? 100 : 0;
                    });
                } else {
                    // Calculate percentages for signals with data
                    result.noData = false;
                    Object.keys(buckets).forEach((key) => {
                        result[key] = (buckets[key] / totalPoints) * 100;
                    });
                }

                return result;
            });
        } catch (error) {
            console.error("Error in chartData useMemo:", error);
            return [];
        }
    }, [
        rawData,
        currentDeviceName,
        currentShadowDevice,
        types,
        localShowThresholds,
    ]);

    const handlePrevDevice = () => {
        setCurrentDeviceIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };

    const handleNextDevice = () => {
        setCurrentDeviceIndex((prev) =>
            prev < deviceList.length - 1 ? prev + 1 : prev,
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            if (!data) return null;
            const isDigital = data.type === "digital";

            return (
                <div className="tw-bg-white tw-p-2 tw-border tw-border-slate-200 tw-shadow-md tw-rounded tw-text-xs">
                    <p className="tw-font-semibold tw-mb-1">{label}</p>
                    {data.noData ? (
                        <div style={{ padding: "4px 0", color: "#999" }}>
                            <em>No Data Available</em>
                        </div>
                    ) : isDigital ? (
                        <>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    marginBottom: "4px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        backgroundColor: data.baseColor,
                                        borderRadius: "2px",
                                    }}
                                />
                                <span>On: {data.on.toFixed(1)}%</span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        backgroundColor: getColorVariant(
                                            data.baseColor,
                                            30,
                                        ),
                                        borderRadius: "2px",
                                    }}
                                />
                                <span>Off: {data.off.toFixed(1)}%</span>
                            </div>
                        </>
                    ) : (
                        <>
                            {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(
                                (pct) => {
                                    const key = `b${pct}`;
                                    const startVal = Math.round(
                                        ((pct - 10) / 100) * 1023,
                                    );
                                    const endVal = Math.round(
                                        (pct / 100) * 1023,
                                    );
                                    const range = `${startVal}-${endVal}`;
                                    const val = data[key];
                                    const bgColor = getColorVariant(
                                        data.baseColor,
                                        pct,
                                    );
                                    if (val === 0) return null;
                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                marginBottom: "2px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "16px",
                                                    height: "16px",
                                                    backgroundColor: bgColor,
                                                    borderRadius: "2px",
                                                }}
                                            />
                                            <span>
                                                {range}: {val.toFixed(1)}%
                                            </span>
                                        </div>
                                    );
                                },
                            )}
                        </>
                    )}
                </div>
            );
        }
        return null;
    };

    const labelFormatter = (value) => {
        if (value > 5) {
            return `${Math.round(value)}%`;
        }
        return null;
    };

    return (
        <ReportResults
            deviceList={deviceList}
            selectedDeviceIndex={validatedDeviceIndex}
            onPrevDevice={handlePrevDevice}
            onNextDevice={handleNextDevice}
            hideEmptyDevices={hideEmptyDevices}
            onHideEmptyDevicesChange={(checked) => {
                setHideEmptyDevices(checked);
                setCurrentDeviceIndex(0);
            }}
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            showThresholds={localShowThresholds}
            onShowThresholdsChange={(value) => {
                setLocalShowThresholds(value);
                if (onShowThresholdsChange) onShowThresholdsChange(value);
            }}
            loading={loading}
            onRefresh={loadData}
            onFavoriteClick={onFavoriteClick}
            onPrintClick={() => printReports(printRef)}
            onDownloadCSV={handleDownloadCSV}
            printRef={printRef}
            isAIAnalysisOpen={showAiPanel}
            isAIAnalyzing={aiAnalyzing}
            aiResults={aiResults}
            aiError={aiError}
            onCloseAIAnalysis={() => {
                setShowAiPanel(false);
                setAiResults(null);
                setAiError(null);
            }}
            onAIFollowUp={(question) => {
                console.log("Follow-up question:", question);
                // Follow-up logic can be implemented here
            }}
        >
            {/* Bar Chart - Conditional rendering */}
            {loading ? (
                <div className="report-loading-container">
                    <span className="report-loading-text">Loading...</span>
                </div>
            ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                        onClick={(data) => {
                            if (
                                data &&
                                data.activePayload &&
                                data.activePayload.length > 0
                            ) {
                                const payload = data.activePayload[0].payload;
                                onBarClick?.({
                                    device: currentDeviceName,
                                    signal: payload.name,
                                });
                            }
                        }}
                    >
                        <defs>
                            {chartData.map((entry, idx) => (
                                <pattern
                                    key={`pattern-${idx}`}
                                    id={`noDataPattern-${idx}`}
                                    patternUnits="userSpaceOnUse"
                                    width="8"
                                    height="8"
                                    patternTransform="rotate(-45)"
                                >
                                    <line
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="8"
                                        stroke={getLightColor(entry.baseColor)}
                                        strokeWidth="4"
                                    />
                                </pattern>
                            ))}
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e0e0e0"
                        />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis
                            domain={[0, 100]}
                            tickFormatter={(val) => `${val}%`}
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(val) =>
                                val === 100 ? "On" : val === 0 ? "Off" : ""
                            }
                            axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        {/* Digital Bars - only for digital signals */}
                        {chartData.some((d) => d.type === "digital") && (
                            <>
                                <Bar
                                    dataKey="off"
                                    stackId="a"
                                    name="Off"
                                    label={labelFormatter}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-off-${index}`}
                                            fill={
                                                entry.type === "digital"
                                                    ? entry.noData
                                                        ? `url(#noDataPattern-${index})`
                                                        : getColorVariant(
                                                              entry.baseColor,
                                                              30,
                                                          )
                                                    : "transparent"
                                            }
                                            stroke={
                                                entry.type === "digital" &&
                                                entry.noData
                                                    ? getLightColor(
                                                          entry.baseColor,
                                                      )
                                                    : undefined
                                            }
                                            strokeDasharray={
                                                entry.type === "digital" &&
                                                entry.noData
                                                    ? "3 3"
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </Bar>
                                <Bar
                                    dataKey="on"
                                    stackId="a"
                                    name="On"
                                    label={labelFormatter}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-on-${index}`}
                                            fill={
                                                entry.type === "digital"
                                                    ? entry.noData
                                                        ? `url(#noDataPattern-${index})`
                                                        : entry.baseColor
                                                    : "transparent"
                                            }
                                            stroke={
                                                entry.type === "digital" &&
                                                entry.noData
                                                    ? getLightColor(
                                                          entry.baseColor,
                                                      )
                                                    : undefined
                                            }
                                            strokeDasharray={
                                                entry.type === "digital" &&
                                                entry.noData
                                                    ? "3 3"
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </Bar>
                            </>
                        )}

                        {/* Analog Bars - only for analog signals */}
                        {chartData.some((d) => d.type !== "digital") &&
                            [10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(
                                (pct) => (
                                    <Bar
                                        key={`b${pct}`}
                                        dataKey={`b${pct}`}
                                        stackId="a"
                                        name={
                                            pct === 100 ? "Analog" : undefined
                                        }
                                        legendType={
                                            pct === 100 ? "rect" : "none"
                                        }
                                        label={labelFormatter}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    entry.type !== "digital"
                                                        ? entry.noData
                                                            ? `url(#noDataPattern-${index})`
                                                            : getColorVariant(
                                                                  entry.baseColor,
                                                                  pct,
                                                              )
                                                        : "transparent"
                                                }
                                                stroke={
                                                    entry.type !== "digital" &&
                                                    entry.noData
                                                        ? getLightColor(
                                                              entry.baseColor,
                                                          )
                                                        : undefined
                                                }
                                                strokeDasharray={
                                                    entry.type !== "digital" &&
                                                    entry.noData
                                                        ? "3 3"
                                                        : undefined
                                                }
                                            />
                                        ))}
                                        )
                                    </Bar>
                                ),
                            )}
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <div className="report-loading-container">
                    <span className="report-loading-text">
                        No data available for selected device
                    </span>
                </div>
            )}
        </ReportResults>
    );
}
